/**
 * What it needs: what the model has to find out before the tool runs, and the rules on it.
 *
 * A LIST AND A DETAIL PANE, AS SETTINGS V2 DRAWS IT, rather than the old dialog's stack of
 * one bordered row per parameter. The list is the thing somebody scans - five parameters,
 * each a mono name, a type and a one-line summary of its rules - and the pane is where one
 * of them is edited with room for its description, which is prompt text the model reads on
 * every turn and deserves more than a one-line box.
 *
 * THE RULES ARE ROWS WITH A CHECKBOX, and a type only offers the rows it can have: a string
 * gets Allowed values, Format, Pattern and Length; a number gets Range; a list gets Item
 * count; a yes/no gets none. Allowed values excludes the other three string rules, because
 * the Worker stores it as an enum and an enum is its own shape (see `toolModel.ts`).
 *
 * STRICT AND VIEW JSON SIT AT THE TOP OF THIS SECTION because both are about this list: strict
 * decides how the model must fill it in, and the JSON is exactly what the model is shown for
 * it. The JSON comes from the Worker's own `functionDefinition`, through
 * `POST /v1/agent/tools/preview`, so the editor never has a second copy of the schema rules
 * that could drift from the one the call uses.
 */

import { useEffect, useState } from "react";
import { ApiError, post } from "../../../api";
import { Plus } from "../../Icons";
import { Segmented, Switch, TextArea, TextInput, Toggle, cx } from "../../../ui";
import { Picker } from "../../Listbox";
import {
  EDITOR_TYPES,
  FORMATS,
  ITEM_TYPES,
  blankParam,
  fromEditor,
  paramSummary,
  rangeWords,
  setType,
  type EditorParam,
  type EditorTool,
} from "./toolModel";

export function ParamsEditor({
  agentId,
  tool,
  onParams,
  onStrict,
}: {
  agentId: string;
  tool: EditorTool;
  onParams: (next: EditorParam[]) => void;
  onStrict: (next: boolean) => void;
}) {
  const params = tool.params;
  const [selected, setSelected] = useState(0);
  const [json, setJson] = useState(false);
  const index = Math.min(selected, Math.max(params.length - 1, 0));
  const param = params[index];

  const update = (patch: Partial<EditorParam>) => onParams(params.map((p, i) => (i === index ? { ...p, ...patch } : p)));

  return (
    <div className="grid gap-3.5">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-2 text-sm font-medium text-ink" title="The AI must send every detail, with exactly these types and values. The rules below are checked when it runs.">
            Strict schema
            <Switch checked={tool.strict} label="Strict schema" onChange={onStrict} />
          </span>
          <button type="button" className="text-sm font-medium text-accent hover:text-ink" onClick={() => setJson(!json)}>
            {json ? "Hide JSON" : "View JSON"}
          </button>
        </div>
      </div>
      {json && <SchemaPreview agentId={agentId} tool={tool} />}

      <div className="grid min-h-80 grid-cols-[240px_minmax(0,1fr)] overflow-hidden rounded-md border border-line max-[900px]:grid-cols-1">
        <div className="flex flex-col border-r border-line-soft bg-ground p-2 max-[900px]:border-r-0 max-[900px]:border-b">
          {params.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setSelected(i)}
              aria-current={i === index ? "true" : undefined}
              className={cx(
                "grid gap-0.5 rounded-sm px-3 py-2.5 text-left",
                i === index ? "bg-accent-soft" : "hover:bg-sunken",
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="truncate font-mono text-sm text-ink">{p.name || "untitled"}</span>
                <span className="flex-none font-mono text-sm text-ink">{p.valuesOn ? "enum" : p.type}</span>
              </span>
              <span className="truncate text-sm text-ink">{paramSummary(p)}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              onParams([...params, blankParam()]);
              setSelected(params.length);
            }}
            className="mt-1 flex items-center gap-1.5 rounded-sm px-3 py-2.5 text-left text-sm font-medium text-accent hover:bg-accent-soft"
          >
            <Plus size={14} />
            Add parameter
          </button>
        </div>

        {param ? (
          <ParamDetail
            key={index}
            param={param}
            strict={tool.strict}
            onChange={update}
            onRemove={() => {
              onParams(params.filter((_, i) => i !== index));
              setSelected(Math.max(index - 1, 0));
            }}
          />
        ) : (
          <p className="p-6 text-sm text-ink">
            No parameters. The AI runs this tool without asking for anything first.
          </p>
        )}
      </div>
    </div>
  );
}

function ParamDetail({
  param,
  strict,
  onChange,
  onRemove,
}: {
  param: EditorParam;
  strict: boolean;
  onChange: (patch: Partial<EditorParam>) => void;
  onRemove: () => void;
}) {
  const [draft, setDraft] = useState("");
  const t = param.type;
  const range = rangeWords(t);
  const enumOn = t === "string" && param.valuesOn;
  const showFormat = param.format !== undefined;
  const showPattern = param.pattern !== undefined;
  const showRange = param.min !== undefined || param.max !== undefined;
  // Only plain text can be a name, a number or an address on the contact.
  const textual = t === "string" && !enumOn;
  const [rangeOn, setRangeOn] = useState(showRange);

  function addValue() {
    const value = draft.trim();
    if (!value || param.values.includes(value)) return;
    onChange({ values: [...param.values, value] });
    setDraft("");
  }

  const number = (raw: string): number | undefined => (raw.trim() === "" ? undefined : Number(raw));

  return (
    <div className="flex min-w-0 flex-col gap-4 px-6 pt-5 pb-5.5">
      <div className="flex items-center gap-3">
        <TextInput
          aria-label="Parameter name"
          className="min-w-0 flex-1 font-mono"
          placeholder="order_number"
          value={param.name}
          onChange={(event) => onChange({ name: event.target.value.replace(/\s+/g, "_").toLowerCase() })}
        />
        <span className="flex flex-none items-center gap-2 text-sm font-medium text-ink">
          Required
          <Switch checked={param.required} label="Required" onChange={(required) => onChange({ required })} />
        </span>
      </div>
      <TextArea
        aria-label="Description"
        rows={2}
        className="w-full resize-none"
        placeholder="Tell the AI what this is and how to ask for it"
        value={param.description}
        onChange={(event) => onChange({ description: event.target.value })}
      />
      <div className="flex flex-wrap items-center gap-3">
        <span className="w-30 flex-none text-sm text-ink">Type</span>
        <Segmented label="Type" value={t} options={EDITOR_TYPES.map((value) => ({ value, label: value }))} onChange={(next) => onChange(setType(param, next))} />
      </div>

      {t === "array" && (
        <div className="flex items-center gap-3 border-t border-line-soft pt-3 text-sm text-ink">
          <span className="w-30 flex-none">Each item</span>
          <Picker
            label="Each item"
            className="font-mono"
            rootClassName="w-36"
            value={param.items}
            options={ITEM_TYPES.map((item) => ({ value: item, label: item }))}
            onChange={(items) => onChange({ items })}
          />
        </div>
      )}

      {t !== "boolean" && (
        <div className="flex flex-col">
          <span className="pt-2 pb-1 text-sm font-medium text-ink">Rules</span>

          {t === "string" && (
            <Rule
              on={enumOn}
              label="Allowed values"
              off="Only accept values from a list"
              onToggle={(on) => onChange(on ? { valuesOn: true, format: undefined, pattern: undefined, min: undefined, max: undefined } : { valuesOn: false, values: [] })}
            >
              <div className="flex flex-wrap items-center gap-1.5">
                {param.values.map((value, i) => (
                  <span key={value} className="inline-flex h-7 items-center gap-0.5 rounded-xs bg-sunken pr-1 pl-2.5 text-sm">
                    {value}
                    <button
                      type="button"
                      aria-label={`Remove ${value}`}
                      className="grid size-5 place-items-center text-ink"
                      onClick={() => onChange({ values: param.values.filter((_, j) => j !== i) })}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  aria-label="Add an allowed value"
                  className="h-7 min-w-38 flex-1 bg-transparent px-1 text-sm outline-none"
                  placeholder="Add value, press Enter"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addValue();
                    }
                    if (event.key === "Backspace" && !draft && param.values.length) onChange({ values: param.values.slice(0, -1) });
                  }}
                />
              </div>
            </Rule>
          )}

          {t === "string" && !enumOn && (
            <Rule on={showFormat} label="Format" off="Date, time, email or ID" onToggle={(on) => onChange({ format: on ? "date" : undefined })}>
              <Picker
                label="Format"
                className="font-mono"
                rootClassName="w-44"
                value={param.format ?? "date"}
                options={FORMATS.map((format) => ({ value: format, label: format }))}
                onChange={(format) => onChange({ format })}
              />
            </Rule>
          )}

          {t === "string" && !enumOn && (
            <Rule on={showPattern} label="Pattern" off="Must match a regular expression" onToggle={(on) => onChange({ pattern: on ? "" : undefined })}>
              <TextInput
                aria-label="Pattern"
                className="font-mono"
                placeholder="regular expression"
                value={param.pattern ?? ""}
                onChange={(event) => onChange({ pattern: event.target.value })}
              />
            </Rule>
          )}

          {!enumOn && (
            <Rule
              on={showRange || rangeOn}
              label={range.label}
              off={range.hint}
              onToggle={(on) => {
                setRangeOn(on);
                if (!on) onChange({ min: undefined, max: undefined });
              }}
            >
              <div className="flex items-center gap-2 text-sm text-ink">
                <TextInput type="number" aria-label="Minimum" className="w-22 font-mono" placeholder="min" value={param.min ?? ""} onChange={(event) => onChange({ min: number(event.target.value) })} />
                to
                <TextInput type="number" aria-label="Maximum" className="w-22 font-mono" placeholder="max" value={param.max ?? ""} onChange={(event) => onChange({ max: number(event.target.value) })} />
                {range.unit}
              </div>
            </Rule>
          )}
        </div>
      )}

      <div className="flex flex-col">
        {/* WHERE THE ANSWER IS FILED, not how it is asked for: the AI never sees this. A
            name, a number or an address lands on the contact's own details; anything else
            becomes a detail of its own, named after this parameter. */}
        <Rule
          on={param.contact !== undefined}
          label="Keep on the contact"
          off="Save the answer on the caller's contact after the call"
          onToggle={(on) =>
            onChange({ contact: on ? (textual ? (param.format === "email" ? "email" : "field") : "field") : undefined })
          }
        >
          <Picker
            label="Keep on the contact as"
            rootClassName="w-60"
            value={param.contact ?? "field"}
            options={CONTACT_KEYS.filter((option) => textual || option.value === "field")}
            onChange={(contact) => onChange({ contact })}
          />
        </Rule>
      </div>

      {strict && !param.required && (
        <p className="text-sm text-ink">
          Optional in strict mode means the AI sends <code className="rounded-xs bg-sunken px-1 font-mono text-sm">null</code> when the caller doesn't give it.
        </p>
      )}
      <div className="flex-1" />
      <button type="button" className="self-start text-sm font-medium text-ink hover:text-alert" onClick={onRemove}>
        Delete parameter
      </button>
    </div>
  );
}

const CONTACT_KEYS: Array<{ value: NonNullable<EditorParam["contact"]>; label: string }> = [
  { value: "field", label: "A detail of its own" },
  { value: "name", label: "Their name" },
  { value: "phone", label: "Their phone number" },
  { value: "email", label: "Their email" },
  { value: "company", label: "Their company" },
  { value: "location", label: "Their address" },
];

/** One rule: a checkbox, its name, and either its control or a line saying what it would do. */
function Rule({
  on,
  label,
  off,
  onToggle,
  children,
}: {
  on: boolean;
  label: string;
  off: string;
  onToggle: (on: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="grid min-h-11.5 grid-cols-[150px_minmax(0,1fr)] items-center gap-2.5 border-t border-line-soft py-1.5 max-[720px]:grid-cols-1">
      <Toggle checked={on} onChange={onToggle} className="text-ink">
        {label}
      </Toggle>
      {on ? children : <span className="text-sm text-ink">{off}</span>}
    </div>
  );
}

/**
 * The function definition the model is shown, from the Worker.
 *
 * DEBOUNCED, because it follows every keystroke in the list beside it, and a refusal is
 * shown in its place rather than as an error: a half-typed pattern that does not compile is
 * a normal state for this box to be in.
 */
function SchemaPreview({ agentId, tool }: { agentId: string; tool: EditorTool }) {
  const [text, setText] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const body = JSON.stringify(fromEditor(tool));

  useEffect(() => {
    let live = true;
    const timer = setTimeout(async () => {
      try {
        const answer = await post<{ definition: string }>("/v1/agent/tools/preview", { agent: agentId, tool: JSON.parse(body) });
        if (!live) return;
        setText(answer.definition);
        setRefusal(null);
      } catch (err) {
        if (!live) return;
        setRefusal(err instanceof ApiError ? err.message : "The preview could not be loaded.");
      }
    }, 400);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [agentId, body]);

  return (
    <div className="grid gap-1.5">
      {refusal && <p className="text-sm text-alert">{refusal}</p>}
      <pre className="max-h-90 overflow-auto rounded-sm bg-sunken px-4 py-3.5 font-mono text-sm leading-relaxed whitespace-pre-wrap text-ink">
        {text ?? "Loading…"}
      </pre>
    </div>
  );
}
